import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readAttachmentContent } from '@evogard/ui';
import type {
  ClaimCafLink,
  ClaimCafTracking,
  ClaimMessage,
  AnalysisForm,
  ClaimActivity,
  ClaimAssigneeOption,
  ClaimComment,
  ClaimFile,
  ClaimMoveResult,
  ClaimPassage,
  CompensationDebtWrite,
  CompensationSummary,
  RepairQuotes,
  RepairSummary,
  SaveRepairQuoteBody,
} from '@evogard/contract';
import { loadFailureMessage, type RepairQuoteSource } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import type { ContactBody, Desk } from './desk.js';

/**
 * A conversa da mesa — da Análise e do Reparo (07/10/2026) — com a API: as
 * rotas de cada departamento vêm do `Desk` (`desk.ts`), e o resto é comum: as passagens, o formulário da
 * fase, os arquivos, a Atividade e os comentários — e mover, desfazer,
 * salvar, anexar e comentar.
 *
 * **Toda chave começa com `['sinistros', 'sinistro']`**, a mesma raiz de
 * `useClaims.ts`, e toda escrita invalida a raiz inteira. Mover muda a fase
 * na lista, na ficha, na trilha e na Atividade de uma vez; comentar muda a
 * Atividade e o contador da aba. Uma escrita que invalidasse só a própria
 * consulta deixaria a aba dizendo uma coisa e o contador outra — foi assim
 * que um comentário gravado deixou de aparecer no topo da Atividade.
 */

const ROOT = ['sinistros', 'sinistro'] as const;

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

export function useDeskPermissions(desk: Desk | null) {
  const { data: context } = useSessionContext();
  return {
    canMove: desk !== null && can(context, desk.resource, 'mover'),
    canComment: can(context, 'claims.notices', 'comentar'),
    /** Quem trabalha a Supervisão do Reparo (07/10/2026). */
    canSupervise:
      desk?.supervision !== undefined &&
      can(context, desk.supervision.resource, desk.supervision.action),
  };
}

export function usePassages(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'passagens', tenantId, id],
    queryFn: async (): Promise<ClaimPassage[]> => {
      const r = await api.claims.listPassages({ params: { id: id! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'a trilha'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/** O formulário de uma fase, pela rota do departamento. */
export function useDeskForm(desk: Desk | null, id: number | null, phase: string | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'formulario', desk?.department, tenantId, id, phase],
    queryFn: async (): Promise<AnalysisForm> => {
      const r = await desk!.api.form(id!, phase!);
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o formulário da fase'));
      return r.body as AnalysisForm;
    },
    enabled: desk !== null && id !== null && phase !== null && tenantId !== undefined,
  });
}

/** O bloco do reparo, no alto da mesa do Reparo, e a gaveta dele. */
export function useRepairSummary(id: number | null, enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'reparo', tenantId, id],
    queryFn: async (): Promise<RepairSummary> => {
      const r = await api.claims.getRepairSummary({ params: { id: id! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o reparo'));
      return r.body;
    },
    enabled: enabled && id !== null && tenantId !== undefined,
  });
}

/**
 * Os dois orçamentos item a item do Reparo — o próprio e o da CILIA
 * (03/11/2026). A conta é `repairQuoteTotals`, feita na tela com a mesma
 * função da API.
 */
export function useRepairQuotes(id: number, enabled = true) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'orcamentos', tenantId, id],
    queryFn: async (): Promise<RepairQuotes> => {
      const r = await api.claims.getRepairQuotes({ params: { id } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o orçamento'));
      return r.body;
    },
    enabled: enabled && tenantId !== undefined,
  });
}

/** Grava um orçamento inteiro — o valor-hora e as linhas. */
export function useSaveRepairQuote(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      source,
      body,
    }: {
      source: RepairQuoteSource;
      body: SaveRepairQuoteBody;
    }): Promise<RepairQuotes> => {
      const r = await api.claims.saveRepairQuote({ params: { id, source }, body });
      if (r.status !== 200) throw refusal(r, 'Não foi possível salvar o orçamento.');
      return r.body;
    },
    onSettled: () => {
      notifyClaimChanged(id);
      return client.invalidateQueries({ queryKey: [...ROOT] });
    },
  });
}

/** O bloco da Indenização — a conta, o prazo e o checklist — e a gaveta dele. */
export function useCompensationSummary(id: number | null, enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'indenizacao', tenantId, id],
    queryFn: async (): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.getCompensationSummary({
        params: { id: id! },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'a conta da indenização'));
      return r.body;
    },
    enabled: enabled && id !== null && tenantId !== undefined,
  });
}

/**
 * Conferir um documento do checklist da Indenização, ou desfazer — muda a
 * situação da Aguardando Documentação, então recarrega tudo e avisa o quadro.
 */
export function useCompensationValidation(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      documentId: number;
      validated: boolean;
    }): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.setCompensationDocumentValidation({
        params: { id, documentId: body.documentId },
        body: { validated: body.validated },
      });
      if (r.status !== 200) throw refusal(r, 'Não foi possível conferir o documento.');
      return r.body;
    },
    onSettled: () => {
      notifyClaimChanged(id);
      return client.invalidateQueries({ queryKey: [...ROOT] });
    },
  });
}

/** Os credores que um débito pode ter — os órgãos públicos e as instituições da base. */
export function useCompensationCreditors(enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'indenizacao', 'credores', tenantId],
    queryFn: async () => {
      const r = await api.claimCompensations.listCompensationCreditorOptions();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os credores'));
      return r.body;
    },
    enabled: enabled && tenantId !== undefined,
  });
}

/**
 * Os débitos da Indenização (§5.9): incluir, corrigir, mudar a situação e o
 * comprovante. Todo ato devolve o bloco com a conta nova; o portão do
 * Pagamento de Débito mora no formulário, então recarrega tudo.
 */
export function useCompensationDebts(id: number) {
  const client = useQueryClient();
  const onSettled = () => {
    notifyClaimChanged(id);
    return client.invalidateQueries({ queryKey: [...ROOT] });
  };
  const create = useMutation({
    mutationFn: async (body: CompensationDebtWrite): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.createCompensationDebt({
        params: { id },
        body,
      });
      if (r.status !== 201) throw refusal(r, 'Não foi possível incluir o débito.');
      return r.body;
    },
    onSettled,
  });
  const update = useMutation({
    mutationFn: async ({
      debtId,
      body,
    }: {
      debtId: number;
      body: CompensationDebtWrite;
    }): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.updateCompensationDebt({
        params: { id, debtId },
        body,
      });
      if (r.status !== 200) throw refusal(r, 'Não foi possível corrigir o débito.');
      return r.body;
    },
    onSettled,
  });
  const setStatus = useMutation({
    mutationFn: async ({
      debtId,
      status,
    }: {
      debtId: number;
      status: string;
    }): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.setCompensationDebtStatus({
        params: { id, debtId },
        body: { status },
      });
      if (r.status !== 200)
        throw refusal(r, 'Não foi possível mudar a situação do débito.');
      return r.body;
    },
    onSettled,
  });
  const attachReceipt = useMutation({
    mutationFn: async ({
      debtId,
      file,
    }: {
      debtId: number;
      file: File;
    }): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.attachCompensationDebtReceipt({
        params: { id, debtId },
        body: {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          content: await readAttachmentContent(file),
        },
      });
      if (r.status !== 201) throw refusal(r, `Não foi possível enviar ${file.name}.`);
      return r.body;
    },
    onSettled,
  });
  const archiveReceipt = useMutation({
    mutationFn: async ({
      debtId,
      documentId,
    }: {
      debtId: number;
      documentId: number;
    }): Promise<CompensationSummary> => {
      const r = await api.claimCompensations.archiveCompensationDebtReceipt({
        params: { id, debtId, documentId },
        body: {},
      });
      if (r.status !== 200) throw refusal(r, 'Não foi possível tirar o comprovante.');
      return r.body;
    },
    onSettled,
  });
  return { create, update, setStatus, attachReceipt, archiveReceipt };
}

/** Quem pode ser responsável no departamento — as pessoas ativas da base. */
export function useAssigneeOptions(desk: Desk | null, enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'responsaveis', desk?.department, tenantId],
    queryFn: async (): Promise<ClaimAssigneeOption[]> => {
      const r = await desk!.api.assigneeOptions();
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'quem pode ser responsável'));
      return r.body as ClaimAssigneeOption[];
    },
    enabled: enabled && desk !== null && tenantId !== undefined,
    staleTime: 5 * 60_000,
  });
}

export function useClaimFiles(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'arquivos', tenantId, id],
    queryFn: async (): Promise<ClaimFile[]> => {
      const r = await api.claims.listClaimFiles({ params: { id: id! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os anexos'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/** As mensagens de envio do sinistro, por departamento — a aba Mensagens (15/10/2026). */
export function useClaimMessages(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'mensagens', tenantId, id],
    queryFn: async (): Promise<ClaimMessage[]> => {
      const r = await api.claims.listClaimMessages({ params: { id: id! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as mensagens'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/** O link da CAF e a trava do botão (16/10/2026). */
export function useCafLink(id: number, enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'caf', tenantId, id],
    queryFn: async (): Promise<ClaimCafLink> => {
      const r = await api.claims.getCafLink({ params: { id } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o link da CAF'));
      return r.body;
    },
    enabled: enabled && tenantId !== undefined,
  });
}

/**
 * Gera um link novo na CAF. Invalida a raiz inteira: o link muda no
 * formulário, o "Script CAF" na aba Mensagens e a Atividade ganha a linha.
 */
export function useRefreshCafLink(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ClaimCafLink> => {
      const r = await api.claims.refreshCafLink({ params: { id }, body: {} });
      if (r.status !== 200) throw refusal(r, 'Não foi possível atualizar o link da CAF.');
      return r.body;
    },
    onSettled: () => client.invalidateQueries({ queryKey: [...ROOT] }),
  });
}

/**
 * Busca o resultado na CAF à mão (16/10/2026) — quando o aviso dela não
 * chegou. Invalida a raiz: os documentos entram nos anexos e a Atividade muda.
 */
/** O acompanhamento da documentação da CAF, para a gaveta da Análise (20/10/2026). */
export function useCafTracking(id: number, enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'caf-tracking', tenantId, id],
    queryFn: async (): Promise<ClaimCafTracking> => {
      const r = await api.claims.getCafTracking({ params: { id } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'a documentação da CAF'));
      return r.body;
    },
    enabled: enabled && tenantId !== undefined,
  });
}

export function useFetchCafResult(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ClaimCafLink> => {
      const r = await api.claims.fetchCafResult({ params: { id }, body: {} });
      if (r.status !== 200)
        throw refusal(r, 'Não foi possível buscar o resultado da CAF.');
      return r.body;
    },
    onSettled: () => client.invalidateQueries({ queryKey: [...ROOT] }),
  });
}

export function useActivity(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'atividade', tenantId, id],
    queryFn: async (): Promise<ClaimActivity[]> => {
      const r = await api.claims.listActivity({ params: { id: id! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'a atividade'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/**
 * A recusa da API, com o código. A mesa precisa distinguir "faltam
 * obrigatórios" de "outra pessoa moveu antes" — o texto sozinho não deixa.
 */
export class DeskRefusal extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly details: Record<string, unknown> | undefined,
  ) {
    super(message);
  }
}

function refusal(r: { status: number; body: unknown }, fallback: string): DeskRefusal {
  const body = r.body as {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
  return new DeskRefusal(body?.message ?? fallback, body?.code ?? null, body?.details);
}

/**
 * Quem precisa saber que um sinistro mudou de lugar — o quadro.
 *
 * O `Kanban` guarda as páginas de cada coluna e não lê as consultas do React
 * Query, então invalidar não o atualiza: o cartão ficava na coluna velha, sem
 * o selo de parado, até alguém recarregar a página (achado no teste do dono
 * em 06/10/2026). Todo ato da mesa que pode mudar a coluna, a situação ou a
 * parada avisa por aqui — mover, desfazer, salvar, anexar, registrar
 * contato, parar e retomar —, venha da mesa aberta ou do aviso no canto.
 */
const claimChangeListeners = new Set<(claimId: number) => void>();

export function onClaimChanged(listener: (claimId: number) => void): () => void {
  claimChangeListeners.add(listener);
  return () => {
    claimChangeListeners.delete(listener);
  };
}

/** Um ato fora da mesa mudou o cartão — etiqueta, vencimento: o quadro recarrega. */
export function notifyClaimChanged(claimId: number) {
  for (const listener of claimChangeListeners) listener(claimId);
}

export function useDeskMutations(desk: Desk | null, id: number) {
  /* Sem mesa (o sinistro num departamento sem uma), as escritas da mesa não
     existem; os comentários continuam. */
  const routes = () => {
    if (!desk) throw new Error('Este departamento ainda não tem mesa.');
    return desk.api;
  };
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: [...ROOT] });
  /** Recarrega as consultas E avisa o quadro — para o que muda onde o sinistro está. */
  const changed = () => {
    for (const listener of claimChangeListeners) listener(id);
    return refresh();
  };

  const move = useMutation({
    mutationFn: async (body: {
      fromPhase: string;
      toPhase: string;
    }): Promise<ClaimMoveResult> => {
      const r = await routes().move(id, body);
      if (r.status !== 200) throw refusal(r, 'Não foi possível mover o sinistro.');
      return r.body as ClaimMoveResult;
    },
    onSettled: changed,
  });

  const undo = useMutation({
    mutationFn: async (passageId: number): Promise<ClaimMoveResult> => {
      const r = await routes().undo(id, passageId);
      if (r.status !== 200) throw refusal(r, 'Não foi possível desfazer.');
      return r.body as ClaimMoveResult;
    },
    onSettled: changed,
  });

  const save = useMutation({
    mutationFn: async ({
      phase,
      values,
    }: {
      phase: string;
      values: Record<string, string | null>;
    }): Promise<AnalysisForm> => {
      const r = await routes().save(id, phase, values);
      if (r.status !== 200) throw refusal(r, 'Não foi possível salvar o formulário.');
      return r.body as AnalysisForm;
    },
    onSuccess: changed,
  });

  /** O responsável do departamento — escolher, trocar ou tirar (`null`). O quadro acompanha. */
  const setAssignee = useMutation({
    mutationFn: async (userId: string | null): Promise<void> => {
      const r = await routes().setAssignee(id, userId);
      if (r.status !== 200) throw refusal(r, 'Não foi possível definir o responsável.');
    },
    onSettled: changed,
  });

  /** Sobe os arquivos um de cada vez — o mesmo laço do aviso (`noticeFiles.ts`). */
  const attach = useMutation({
    mutationFn: async ({
      phase,
      field,
      files,
    }: {
      phase: string;
      field: string;
      files: { file: File; typeMime: string }[];
    }): Promise<void> => {
      for (const { file, typeMime } of files) {
        const content = await readAttachmentContent(file);
        const r = await routes().attach(id, phase, field, {
          fileName: file.name,
          mimeType: typeMime,
          content,
        });
        if (r.status !== 201) throw refusal(r, `Não foi possível enviar ${file.name}.`);
      }
    },
    onSettled: changed,
  });

  const archiveFile = useMutation({
    mutationFn: async ({
      phase,
      field,
      documentId,
    }: {
      phase: string;
      field: string;
      documentId: number;
    }): Promise<void> => {
      const r = await routes().archive(id, phase, field, documentId);
      if (r.status !== 200) throw refusal(r, 'Não foi possível tirar o arquivo.');
    },
    onSettled: changed,
  });

  const comment = useMutation({
    mutationFn: async (body: string): Promise<ClaimComment> =>
      ok(
        await api.claims.createComment({ params: { id }, body: { body } }),
        201,
        'Não foi possível comentar.',
      ),
    onSuccess: refresh,
  });

  const editComment = useMutation({
    mutationFn: async ({ commentId, body }: { commentId: number; body: string }) =>
      ok(
        await api.claims.updateComment({ params: { id, commentId }, body: { body } }),
        200,
        'Não foi possível editar o comentário.',
      ),
    onSuccess: refresh,
  });

  const removeComment = useMutation({
    mutationFn: async (commentId: number) =>
      ok(
        await api.claims.archiveComment({ params: { id, commentId }, body: {} }),
        200,
        'Não foi possível remover o comentário.',
      ),
    onSuccess: refresh,
  });

  /* Os registros que não mudam a etapa (06/10/2026). */
  const registerContact = useMutation({
    mutationFn: async ({
      print,
      ...body
    }: Omit<ContactBody, 'attachment'> & {
      /** A evidência escolhida na tela — sobe junto, em base64. */
      print: { file: File; typeMime: string };
    }): Promise<ClaimMoveResult> => {
      const r = await routes().contact(id, {
        ...body,
        attachment: {
          fileName: print.file.name,
          mimeType: print.typeMime,
          content: await readAttachmentContent(print.file),
        },
      });
      if (r.status !== 201) throw refusal(r, 'Não foi possível registrar o contato.');
      return r.body as ClaimMoveResult;
    },
    onSettled: changed,
  });

  const hold = useMutation({
    mutationFn: async (body: {
      fromPhase: string;
      type: 'pendencia' | 'pre_desistencia' | 'negado_com_posse' | 'juridico';
      /** A mensagem ou a justificativa; o negado com posse não leva (16/10/2026). */
      text?: string;
      /** O tipo da pendência, onde o departamento os tem (Indenização, I2). */
      pendencyKind?: string;
    }): Promise<ClaimMoveResult> => {
      const r = await routes().hold(id, body);
      if (r.status !== 201) throw refusal(r, 'Não foi possível parar o sinistro.');
      return r.body as ClaimMoveResult;
    },
    onSettled: changed,
  });

  const resume = useMutation({
    mutationFn: async (body: {
      fromPhase: string;
      note?: string;
    }): Promise<ClaimMoveResult> => {
      const r = await routes().resume(id, body);
      if (r.status !== 200) throw refusal(r, 'Não foi possível retomar o sinistro.');
      return r.body as ClaimMoveResult;
    },
    onSettled: changed,
  });

  return {
    move,
    undo,
    save,
    attach,
    archiveFile,
    comment,
    editComment,
    removeComment,
    registerContact,
    hold,
    resume,
    setAssignee,
  };
}

/** Uma assinatura de arquivo do sinistro, pedida no clique. */
export async function claimFileUrl(claimId: number, documentId: number): Promise<string> {
  const r = await api.claims.documentUrl({ params: { id: claimId, documentId } });
  return ok(r, 200, 'Não foi possível abrir o arquivo.').url;
}
